import { useCallback, useEffect, useState } from "react";
import QaIssuesPanel from "./QaIssuesPanel";
import QaAutoControl from "./QaAutoControl";
import QaAutoRuns from "./QaAutoRuns";
import QaDesktopPicker from "./QaDesktopPicker";
import { getQaAvailability, setQaAvailability } from "../api/qaAvailability";
import { useMe } from "../hooks/useMe";

type Props = {
  project: string;
  /** Display name for the project; the header falls back to the id when unknown. */
  projectName?: string;
  onClose: () => void;
};

// Global "Availability" toggle for Emil as a whole. Disabling stops every QA
// lane from dequeuing (and aborts the in-flight desktop job) so a human can take
// the physical desktop; queued jobs stay queued and drain on re-enable. It does
// NOT stop the GitHub code review: the pr-reviewer only consults this switch at
// its QA gate, which sits AFTER review/fix/verify, so a PR still gets its full
// review and simply parks at `qa_blocked` until QA returns. Optimistic flip on
// click, then a refetch reconciles against the qa-agent's actual state. Inert
// when the daemon has no qa-agent configured.
function QaAvailabilityControl() {
  const [enabled, setEnabled] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const a = await getQaAvailability();
      setEnabled(a.enabled);
      setConfigured(a.configured);
    } catch { /* keep last-known state */ }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const toggle = useCallback(async () => {
    const next = !enabled;
    setBusy(true);
    setEnabled(next); // optimistic
    try {
      const a = await setQaAvailability(next);
      setEnabled(a.enabled);
      setConfigured(a.configured);
    } catch {
      await refresh(); // reconcile on failure
    } finally {
      setBusy(false);
    }
  }, [enabled, refresh]);

  if (!configured) {
    return (
      <div className="qa-availability qa-availability-off qa-availability-unconfigured">
        <span className="qa-availability-pill">Emil: unavailable</span>
        <span className="qa-availability-note">Emil is not configured</span>
      </div>
    );
  }

  return (
    <div className={`qa-availability ${enabled ? "qa-availability-on" : "qa-availability-off"}`}>
      <span className="qa-availability-pill">
        {enabled ? "Emil: Available" : "Emil: Disabled — desktop in use"}
      </span>
      <button
        type="button"
        className="qa-availability-toggle"
        onClick={() => void toggle()}
        disabled={busy}
        aria-pressed={enabled}
        aria-label={enabled ? "Disable Emil" : "Enable Emil"}
      >
        {enabled ? "Disable" : "Enable"}
      </button>
      <span className="qa-availability-note">
        Global — affects all projects. Stops Emil's in-game test runs only; GitHub code review keeps running. Queued QA resumes when re-enabled.
      </span>
    </div>
  );
}

// Full-page Emil view. QA defects are project-level (not tied to a single
// conversation), so the panel lives on its own route (/qa/<project>) — a hard
// reload restores it instead of bouncing back to the chat dashboard.
export default function QaPage({ project, projectName, onClose }: Props) {
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  return (
    <div className="qa-page">
      <header className="qa-page-header">
        <button type="button" className="qa-page-back" onClick={onClose} aria-label="Back to chat">←</button>
        <h1>Emil · {projectName ?? project}</h1>
        <QaAvailabilityControl />
        {/* Directly beneath the Availability toggle: both are global Emil
            controls, and which machine the lane drives only matters while the
            agent is available. */}
        <QaDesktopPicker />
        <QaAutoControl project={project} isAdmin={isAdmin} />
      </header>
      <QaAutoRuns project={project} isAdmin={isAdmin} />
      <QaIssuesPanel project={project} active={true} />
    </div>
  );
}
